import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';


export default function NotificationManagement({ notificationView = '' }) {
  const user = JSON.parse(localStorage.getItem('user'));
  const [recipients, setRecipients] = useState({ users: [], customers: [] });
  const [notifyMode, setNotifyMode] = useState('single');
  const [notifyTarget, setNotifyTarget] = useState('');
  const [notifyCategory, setNotifyCategory] = useState('');
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyVoice, setNotifyVoice] = useState(true);
  const [notifyVoiceText, setNotifyVoiceText] = useState('');
  const [loading, setLoading] = useState(false);

  const canSendAppNotifications = ['admin', 'whitelabel_admin'].includes(user?.role);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchRecipients = useCallback(async () => {
    if (!canSendAppNotifications) {
      setRecipients({ users: [], customers: [] });
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/notifications/recipients`, { headers: getHeaders() });
      setRecipients(res.data);
    } catch (err) {
      console.log(err);
    }
  }, [canSendAppNotifications, getHeaders]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const inp = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151' };
  const formatRole = (r = '') => (r === 'whitelabel_admin' ? 'Admin' : r.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()));

  const recipientOptions = [
    ...recipients.users.map(u => ({ value: `user:${u.id}`, label: `${u.name} - ${formatRole(u.role)} - ${u.mobile}` })),
    ...recipients.customers.map(c => ({ value: `customer:${c.id}`, label: `${c.name} - Customer - ${c.mobile}${c.vehicle_no ? ` - ${c.vehicle_no}` : ''}` })),
  ];
  const roleCount = (category) => recipients.users.filter(u => u.role === category).length;
  const categoryOptions = [
    { value: 'employee', label: 'Employees', count: roleCount('employee') },
    { value: 'dealer', label: 'Dealers', count: roleCount('dealer') },
    { value: 'customer', label: 'Customer App Users', count: roleCount('customer') },
    { value: 'crm_customer', label: 'All CRM Customers', count: recipients.customers.length },
  ];
  const notificationTemplates = [
    {
      key: 'renewal',
      label: 'Renewal Reminder',
      title: 'Renewal Reminder',
      message: 'Hello {name}, your vehicle renewal is due. Please complete your renewal on time.',
      voice: 'Hello {name}, your vehicle renewal is due. Please complete your renewal on time.',
    },
    {
      key: 'info',
      label: 'Information',
      title: 'Important Information',
      message: 'Hello {name}, you have an important update. Please check the app notification.',
      voice: 'Hello {name}, you have an important update. Please check the app notification.',
    },
  ];

  useEffect(() => {
    if (!notificationView) return;
    if (notificationView === 'notification-single') {
      setNotifyMode('single');
      setNotifyTarget('');
      setNotifyCategory('');
    } else if (notificationView === 'notification-category') {
      setNotifyMode('category');
      setNotifyTarget('');
      setNotifyCategory('');
    } else if (notificationView === 'notification-template-renewal') {
      applyNotificationTemplate(notificationTemplates[0]);
    } else if (notificationView === 'notification-template-info') {
      applyNotificationTemplate(notificationTemplates[1]);
    } else if (notificationView === 'notification-voice-enabled') {
      setNotifyVoice(true);
    } else if (notificationView === 'notification-voice-disabled') {
      setNotifyVoice(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationView]);

  const applyNotificationTemplate = (template) => {
    setNotifyTitle(template.title);
    setNotifyMessage(template.message);
    setNotifyVoice(true);
    setNotifyVoiceText(template.voice);
  };

  const resetNotificationForm = () => {
    setNotifyTarget('');
    setNotifyCategory('');
    setNotifyTitle('');
    setNotifyMessage('');
    setNotifyVoice(true);
    setNotifyVoiceText('');
  };

  const sendNotification = async (e) => {
    e.preventDefault();
    if (!canSendAppNotifications) return alert('Only Admin can send app notifications');
    if (notifyMode === 'single' && !notifyTarget) return alert('Please select a user or customer');
    if (notifyMode === 'category' && !notifyCategory) return alert('Please select a user category');
    const [targetType, targetId] = notifyMode === 'single' ? notifyTarget.split(':') : ['category', notifyCategory];

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/notifications/send`, {
        target_type: targetType,
        target_id: targetId,
        target_category: notifyMode === 'category' ? notifyCategory : undefined,
        title: notifyTitle,
        message: notifyMessage,
        voice_enabled: notifyVoice,
        voice_text: notifyVoiceText || notifyMessage,
      }, { headers: getHeaders() });
      alert(res.data?.message || 'Notification sent successfully!');
      resetNotificationForm();
    } catch (err) {
      alert(err.response?.data?.message || 'Notification failed');
    } finally {
      setLoading(false);
    }
  };

  if (!canSendAppNotifications) {
    return (
      <div style={{ padding: '24px' }}>
        <h2 style={{ color: '#1e3a5f', marginTop: 0 }}>Notification</h2>
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid #e5e7eb', color: '#64748b' }}>
          Only Admin can send app notifications.
        </div>
      </div>
    );
  }

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ color: '#1e3a5f', margin: 0 }}>Notification</h2>
        <button type="button" onClick={fetchRecipients} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
          Refresh
        </button>
      </div>
      </div>

      <div className="fixed-panel-body">
      <div style={{ background: 'linear-gradient(135deg, #f7fcff, #f1fbdf)', border: '1px solid #cde7f8', borderRadius: '8px', padding: '18px', marginBottom: '20px', boxShadow: '0 2px 12px rgba(6,70,120,0.08)' }}>
        <h3 style={{ color: '#063e73', margin: '0 0 12px' }}>Send app notification to users or customers</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {notificationTemplates.map(template => (
            <button key={template.key} type="button" onClick={() => applyNotificationTemplate(template)} style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #cde7f8', background: 'white', color: '#063e73', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {template.label}
            </button>
          ))}
        </div>
        <form onSubmit={sendNotification}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={lbl}>Send To *</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {[
                  { key: 'single', label: 'Single User' },
                  { key: 'category', label: 'User Category' },
                ].map(item => (
                  <button key={item.key} type="button" onClick={() => { setNotifyMode(item.key); setNotifyTarget(''); setNotifyCategory(''); }} style={{ padding: '8px 14px', borderRadius: '999px', border: '1px solid #cde7f8', cursor: 'pointer', background: notifyMode === item.key ? '#063e73' : 'white', color: notifyMode === item.key ? 'white' : '#374151', fontWeight: 'bold', fontSize: '13px' }}>
                    {item.label}
                  </button>
                ))}
              </div>
              {notifyMode === 'single' ? (
                <select style={inp} value={notifyTarget} onChange={e => setNotifyTarget(e.target.value)} required>
                  <option value="">Select recipient</option>
                  {recipientOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              ) : (
                <select style={inp} value={notifyCategory} onChange={e => setNotifyCategory(e.target.value)} required>
                  <option value="">Select category</option>
                  {categoryOptions.map(item => <option key={item.value} value={item.value}>{item.label} ({item.count})</option>)}
                </select>
              )}
            </div>
            <div>
              <label style={lbl}>Title *</label>
              <input style={inp} value={notifyTitle} onChange={e => setNotifyTitle(e.target.value)} placeholder="Notification title" required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={lbl}>Text Message *</label>
              <textarea style={{ ...inp, minHeight: '76px', resize: 'vertical' }} value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)} placeholder="Message that user will see in app" required />
            </div>
            <div>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={notifyVoice} onChange={e => setNotifyVoice(e.target.checked)} />
                Send with voice
              </label>
              <textarea style={{ ...inp, minHeight: '76px', resize: 'vertical' }} value={notifyVoiceText} onChange={e => setNotifyVoiceText(e.target.value)} placeholder="Voice text, blank means same as message" disabled={!notifyVoice} />
            </div>
          </div>
          <button type="submit" disabled={loading} style={{ marginTop: '12px', background: loading ? '#d1d5db' : '#8cc63f', color: '#082f16', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
            {loading ? 'Sending...' : 'Send Notification'}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
}
